import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import profileService from '@services/profileService';
import type { Profile, ProfileFeatures } from '@/types/planning';
import { ACCESS_EXPIRED_EVENT } from '@/utils/apiError';

/**
 * Perfil do usuário nesta aplicação (GET /auth/profile), carregado uma vez depois do login.
 * Não existe equipe nem hierarquia: `isAdmin` só libera as telas de Administração.
 * `features.cac` (liberação do Acervo CAC) vem do backend; o admin sempre tem.
 */
interface ProfileContextType {
  profile: Profile | null;
  isAdmin: boolean;
  /** Módulos liberados (`features` do /auth/profile). `cac` esconde/mostra o menu Acervo CAC. */
  features: ProfileFeatures;
  /** Período de teste encerrado (`access_status = expired`): o DashboardLayout mostra a tela de acesso encerrado. */
  isAccessExpired: boolean;
  isLoading: boolean;
  reload: () => Promise<void>;
}


const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const reload = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      setProfile(await profileService.getProfile());
    } catch (err) {
      // O perfil é complementar: sem ele a tela continua funcionando pelas permissões do token.
      console.warn('Falha ao carregar /auth/profile:', err);
      if (!silent) setProfile(null);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Qualquer chamada que receba 403 `access_expired` (apiErrorFrom) avisa por evento: recarrega o
  // perfil em silêncio para `access_status` virar `expired` e a tela de acesso encerrado assumir.
  useEffect(() => {
    const onExpired = () => {
      reload(true);
    };
    window.addEventListener(ACCESS_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(ACCESS_EXPIRED_EVENT, onExpired);
  }, [reload]);

  const value = useMemo<ProfileContextType>(
    () => ({
      profile,
      isAdmin: !!profile?.is_admin,
      // Admin sempre tem o CAC (regra do backend); sem perfil carregado, nada é liberado.
      features: { cac: !!(profile?.features?.cac || profile?.is_admin) },
      isAccessExpired: profile?.access_status === 'expired',
      isLoading,
      reload: () => reload(false),
    }),
    [profile, isLoading, reload],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProfile(): ProfileContextType {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile deve ser usado dentro de um ProfileProvider');
  }
  return context;
}
